import { getMapAssets } from '../../domain/map/mapAssets.js';
import {
  worldToMinimapPx,
  bridgeSilhouetteBand,
  MINIMAP_ISO_DEG
} from '../../domain/map/minimapProjection.js';

const TEAM_FILL = {
  blue: '#3b82f6',
  red: '#ef4444',
  neutral: '#fbbf24'
};

const KIND_RADIUS = {
  champion: 5,
  structure: 4,
  minion: 2.5,
  relic: 3,
  ping: 4,
  other: 2.5
};

const PING_FILL = {
  danger: '#ef4444',
  on_my_way: '#38bdf8',
  assist: '#4ade80',
  missing: '#fbbf24'
};

/**
 * createMinimapController — Bottom-right minimap HUD (AR-06).
 * Bridge maps use iso 45° projection to match the live Murder Bridge view.
 */
export function createMinimapController({
  rootEl,
  canvasEl,
  getBounds,
  getMarkers
} = {}) {
  let mapId = null;

  function viewOptions(assets) {
    return {
      angleDeg: assets.silhouette === 'bridge' ? MINIMAP_ISO_DEG : 0
    };
  }

  function getMapId() {
    return mapId;
  }

  function setMap(nextMapId) {
    mapId = nextMapId || null;
    if (!rootEl || !mapId) {
      hide();
      return;
    }
    const assets = getMapAssets(mapId);
    rootEl.hidden = false;
    rootEl.setAttribute('aria-hidden', 'false');
    // Procedural canvas matches live layout; wiki PNG orientation differs.
    rootEl.style.backgroundImage = 'none';
    rootEl.style.backgroundColor = assets.silhouette === 'bridge' ? '#070b12' : '#0a1628';
    if (rootEl.dataset) {
      rootEl.dataset.mapId = mapId;
      rootEl.dataset.silhouette = assets.silhouette || '';
      rootEl.dataset.angleDeg = String(viewOptions(assets).angleDeg);
    }
    sync();
  }

  function hide() {
    if (!rootEl) return;
    rootEl.hidden = true;
    rootEl.setAttribute('aria-hidden', 'true');
    rootEl.style.backgroundImage = '';
  }

  function drawBridgeBand(ctx, band) {
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(51, 65, 85, 0.85)';
    ctx.lineWidth = band.lineWidth;
    ctx.beginPath();
    ctx.moveTo(band.x1, band.y1);
    ctx.lineTo(band.x2, band.y2);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(56, 189, 248, 0.4)';
    ctx.lineWidth = band.lineWidth * 0.45;
    ctx.beginPath();
    ctx.moveTo(band.x1, band.y1);
    ctx.lineTo(band.x2, band.y2);
    ctx.stroke();
  }

  function sync() {
    if (!canvasEl || !mapId) return;
    const ctx = canvasEl.getContext('2d');
    if (!ctx) return;
    const w = canvasEl.width;
    const h = canvasEl.height;
    ctx.clearRect(0, 0, w, h);

    const assets = getMapAssets(mapId);
    const bounds = typeof getBounds === 'function' ? getBounds() : null;
    if (!bounds) return;
    const opts = viewOptions(assets);

    if (assets.silhouette === 'bridge') {
      drawBridgeBand(ctx, bridgeSilhouetteBand(bounds, w, h, opts));
    }

    const markers = typeof getMarkers === 'function' ? getMarkers() : [];
    for (const marker of markers) {
      const pos = worldToMinimapPx(marker.x, marker.z, bounds, w, h, opts);
      const r = KIND_RADIUS[marker.kind] || KIND_RADIUS.other;
      ctx.beginPath();
      ctx.arc(pos.px, pos.py, r, 0, Math.PI * 2);
      ctx.fillStyle = marker.kind === 'ping'
        ? (PING_FILL[marker.pingType] || TEAM_FILL.neutral)
        : (TEAM_FILL[marker.team] || TEAM_FILL.neutral);
      ctx.fill();
    }
  }

  return { setMap, sync, hide, getMapId };
}
